// © 2026 AttainIP Engineering
//
// Shared tooltip pill styling — the single source of truth for how a tooltip
// LOOKS, used by both the global TooltipHost (data-tooltip strings) and
// PortalTooltip (rich content). Rules: comfortable max-width with wrapping, so
// the pill hugs its content up to a readable measure, then wraps.

import type { CSSProperties } from 'react';

/** Comfortable reading measure — pill hugs content up to this, then wraps. */
export const TOOLTIP_MAX_WIDTH = 320;

// ── Show delays (ms) ────────────────────────────────────────────────────────
// Tooltips come in two timing tiers. Content the user is reading — source and
// disclosure refs ([1]/[2] paragraph markers, chart dots, status) — should feel
// near-instant. UI-affordance and keyboard-shortcut *hints* (sidebar nav, split
// toggle, edit pencils, menu items) should wait a beat so they don't flash on a
// quick pass. TooltipHost defaults to INSTANT; opt an element into the slower
// tier with `data-tooltip-delay="hint"`. PortalTooltip defaults to HINT (most
// of its uses are toolbar/shortcut hints); disclosure surfaces pass INSTANT.
/** Source/disclosure — near-instant. */
export const TOOLTIP_DELAY_INSTANT = 60;
/** Shortcut / UI-affordance hints — a deliberate beat before showing. */
export const TOOLTIP_DELAY_HINT = 400;

/** The pill's own box styling (colour + shape + wrapping). Positioning is
 *  supplied separately by the caller (fixed top/left from positionTooltip).
 *  ONE house style — the dark ink-900 pill. Tooltips that need a distinct look
 *  (e.g. the claims-chart dot's yellow accent line) supply bespoke `content`
 *  to PortalTooltip rather than a colour variant. */
export function tooltipPillStyle(): CSSProperties {
  return {
    background: 'var(--color-ink-900)',
    color: 'var(--color-white, #fff)',
    fontFamily: 'var(--font-ui)',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.35,
    textAlign: 'left',
    padding: '6px 8px',
    borderRadius: 4,
    maxWidth: TOOLTIP_MAX_WIDTH,
    width: 'max-content',
    // Wrap once content would exceed the max measure; honour explicit newlines.
    whiteSpace: 'pre-line',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
    pointerEvents: 'none',
  };
}
